import { app } from '../app/app.js';
import { assert, error, log } from '../app/logger.js';
import { navigation } from '../app/navigation.js';
import { services } from '../app/services.js';
import { settings } from '../app/settings.js';
import { Location } from '../data/location.js';
import { PanelBase } from '../ui/panel_base.js';
import { listenToEvent } from '../util/listen_to_event.js';
import { waitFor } from '../util/util.js';
import { window } from '../window.js';
import { Coords } from './coords.js';
import { InfoWindow } from './infowindow.js';
import { mapHandler } from './map_handler.js';
import { MapMediaLayer } from './map_media_layer.js';

import './controls_bar.js';
import './map_loc_stick_panel.js';
import './mapsearch_control.js';
import './track_edit_toolbar.js';

let g_map = null;

let g_topLeftContainer = null;
let g_controlsBar = null;
let g_autocomplete = null;
let g_infoWindow = null;
let g_locInfoPanel = null;

let g_ignoreClickOnMap = false;
let g_onacceptedCallback = null;

function getMap() {
  return mapHandler.getMap();
}

/**
 * Maps panel.
 */
class MapPanel extends PanelBase {
  static id = navigation.kMapPanelId;

  static ownsPage(page) {
    return /maps(\/loc\/\d+)?$/.test(page);
  }

  constructor() {
    super();

    this.callbacks = {};

    settings.hookOnChange(
      'mediaTimeOffset',
      timeOffset => this.active && this.#mapMediaLayer?.showPics(timeOffset)
    );
  }

  prepare() {
    this.setConsumer('explore-maps-create-trip', loc =>
      app.authcall(() => navigation.tripPlanning.planTrip(loc))
    );
  }

  async switchOn(params) {
    log(`[panel:switch][map:open] open map`);
    assert(
      params,
      `[panel:switch][map:open] no params were given for MapPanel`
    );

    this.params = params;

    document.onkeydown = ev => {
      if (ev.key == 'Escape') {
        if (g_locInfoPanel.closeIfOpen()) {
          ev.stopPropagation();
          return;
        }
        if (g_infoWindow.closeIfOpen()) {
          ev.stopPropagation();
          return;
        }
        navigation.closeMap();
      }
    };
    this.#keyUpHandler = listenToEvent(document, 'keyup', ev => {
      if (ev.key == 'Backspace') {
        this.#deleteTrack();
      }
    });

    // Initialize map and put the map element under the hierarchy of the panel
    // control.
    if (!this.#mapPromise) {
      this.#mapPromise = mapHandler.attachMapIfNot(this).then(map => {
        assert(map, `[panel:switch][map:open] no map`);
        g_map = map;
        this.initMap();
        return map;
      });
    }

    super.switchOn();
    await this.#mapPromise;

    // The panel was switched off during map initialization.
    if (!this.active) {
      return;
    }

    g_map.locs.on_marker_click = loc => this.on_marker_click(loc);
    g_map.locs.on_marker_dblclick = loc => this.on_marker_dblclick(loc);

    g_onacceptedCallback = this.callbacks[params.consumerId];

    // Position the map.
    if (params.locId) {
      this.showUpLocation({ id: params.locId }); // async
    } else {
      g_locInfoPanel.close();
      services.location.getDefaultLocation().then(loc => {
        if (!loc) {
          error(loc, `[map] no default location`);
          return;
        }

        // Move the map to IP location if the user didn't moved them map yet.
        if (
          g_map.zoom == g_map.defaultZoom &&
          g_map.center.lat == g_map.defaultCenter.lat &&
          g_map.center.lng == g_map.defaultCenter.lng
        ) {
          log(`[map] center map at default location (${loc.lat}, ${loc.lng})`);
          g_map.panTo(loc);
        }
      });
    }

    g_autocomplete.value = '';

    // No map-search focus if the pic popup is shown.
    if (this.picPopup) {
      return;
    }

    // Do not focus on mobile and tables to avoid on-screen keyboard popup.
    // Wait unit layout is ready to give that information.
    let pointer = await waitFor(
      () => {
        const fine = window.matchMedia('(pointer:fine)').matches;
        const coarse = window.matchMedia('(pointer:coarse)').matches;
        return fine || coarse
          ? {
              fine,
              coarse,
            }
          : null;
      },
      300,
      `[panel:switch][map:open] wait for media pointer`
    );

    if (pointer && pointer.fine) {
      log(`[panel:switch][map:open]: MapsSearchControl.focus`);
      g_autocomplete.focus();
    } else {
      log(
        `[panel:switch][map:open]: no MapsSearchControl.focus on mobile or tablets,
  pointer fine: ${window.matchMedia('(pointer:fine)').matches},
  pointer coarse: ${window.matchMedia('(pointer:coarse)').matches}
  pointer none: ${window.matchMedia('(pointer:none)').matches}`
      );
    }
  }

  async switchOff() {
    log(`[panel:switch][map:close] close map`);

    document.onkeydown = null;
    this.#keyUpHandler.remove();
    this.#keyUpHandler = null;

    // Exit fullscreen before we close the map (Safari doesn't survive that).
    await exitFullscreen();

    // Close/supress popups to release temporary markers and suppress all
    // background jobs they may be doing.
    g_infoWindow.closeIfOpen();
    g_locInfoPanel.supress();

    super.switchOff();
  }

  get active() {
    return !!document.onkeydown;
  }

  getLocPage(loc) {
    return `maps/loc/${loc.id}`;
  }

  get pageTitle() {
    return (this.params && this.params.pageTitle) || `Maps | CamperAid`;
  }

  /**
   * Open a side panel for a location.
   */
  async showUpLocation({ id }) {
    let loc = await g_map.locs.load(id);
    if (loc.gpxTracks && loc.gpxTracks.length > 0) {
      // TODO: multiple routes are not supported.
      if (this.#activeTrack && loc.gpxTracks[0] != this.#activeTrack) {
        this.#activeTrack.active = false;
      }
      this.#activeTrack = loc.gpxTracks[0];
    }

    g_infoWindow.closeIfOpen();
    g_locInfoPanel.show(loc, {
      panAndZoom: true,
    });
  }

  /**
   * Open an info window for the google place.
   */
  showUpInfoWindow(loc) {
    assert(!loc.id, '[map] no info window for existing location');
    g_locInfoPanel.closeIfOpen();
    g_infoWindow.show(loc);
  }

  static markup = `
    <div id='map-control-toolbars'>
      <track-edit-toolbar hidden></track-edit-toolbar>
      <map-controlsbar></map-controlsbar>
    </div>
  `;

  static get selectors() {
    return Object.assign({}, super.selectors, {
      TrackEditToolbar: `track-edit-toolbar`,
    });
  }

  static get elements() {
    return {
      trackEditToolbar: this.selectors.TrackEditToolbar,
    };
  }

  get historyState() {
    let { locId } = this.params;
    return [
      {
        page: (locId && this.getLocPage({ id: locId })) || this.page,
        params: this.params,
      },
      this.pageTitle,
      `${window.location.origin}/maps`,
    ];
  }

  setConsumer(consumerId, onacceptedCallback) {
    this.callbacks[consumerId] = onacceptedCallback;
  }

  async addMedia(files) {
    if (!this.#mapMediaLayer) {
      this.#mapMediaLayer = new MapMediaLayer(await this.#mapPromise);
    }
    return this.#mapMediaLayer.add(files);
  }

  get picPopup() {
    return (this.#mapMediaLayer && this.#mapMediaLayer.picPopup) || null;
  }

  get picMarkers() {
    return (this.#mapMediaLayer && this.#mapMediaLayer.picMarkers) || new Map();
  }

  get gpxCollections() {
    return (this.#mapMediaLayer && this.#mapMediaLayer.gpxCollections) || [];
  }

  initialize() {
    super.initialize();
    this.classList.add('fixed');

    listenToEvent(this.trackEditToolbar, 'trackdelete', () =>
      this.#deleteTrack()
    );
  }

  initMap() {
    log(`[map] initialize map`);

    // Add map event listeners.
    for (let eventName of [
      'click',
      'mousedown',
      'dblclick',
      'mousemove',
      'trackactive',
      'trackclick',
      'trackinactive',
    ]) {
      g_map.on(eventName, ev => {
        if (this.active) {
          this[`onmap_${eventName}`](ev);
        }
      });
    }

    // Top left controls container.
    g_topLeftContainer = document.createElement('div');
    g_topLeftContainer.id = 'map-info-topleftcontrols-container';

    // Info window.
    g_infoWindow = new InfoWindow(g_map);
    g_infoWindow.onnewloc = window.map_addLocation;

    // Search location widget.
    g_autocomplete = document.createElement('mapsearch-control');
    g_autocomplete.map = g_map;
    listenToEvent(g_autocomplete, 'itemselect', ev => {
      // Either the location is internal (i.e. it has id) or
      // there's a known location matching google ID
      // (the case when autocomplete search results weren't requeried).
      let loc =
        (!ev.detail.id && g_map.locs.byGoogleId(ev.detail.google_id)) ||
        ev.detail;
      log(`[map:search] location picked '${loc.name}':${loc.id}`);
      return loc.id ? this.showUpLocation(loc) : this.showUpInfoWindow(loc);
    });
    g_autocomplete.onfocus = () => g_infoWindow.closeIfOpen();
    g_topLeftContainer.appendChild(g_autocomplete);

    // Locinfo side panel.
    g_locInfoPanel = document.createElement('map-loc-stick-panel');
    g_locInfoPanel.setup(g_map);

    listenToEvent(g_locInfoPanel, 'close', () => this.#locSidePanelClosed());
    listenToEvent(g_locInfoPanel, 'locchange', ev =>
      this.#locChanged(ev.detail)
    );
    listenToEvent(g_locInfoPanel, 'locdelete', ev =>
      this.#locDeleted(ev.detail)
    );
    listenToEvent(g_locInfoPanel, 'locpick', ev =>
      window.map_locationAccepted(ev.detail)
    );
    listenToEvent(g_locInfoPanel, 'locreport', ev =>
      this.#locReported(ev.detail)
    );
    listenToEvent(g_locInfoPanel, 'show', ev =>
      this.#locSidePanelShown(ev.detail)
    );

    g_topLeftContainer.appendChild(g_locInfoPanel);

    this.appendChild(g_topLeftContainer);

    // Controls bar
    g_controlsBar = this.querySelector('map-controlsbar');
    g_controlsBar.map = g_map;

    log(`[map] map initialized`);
  }

  onmap_click(ev) {
    if (!this.active) {
      return;
    }

    if (g_ignoreClickOnMap) {
      g_ignoreClickOnMap = false;
      return;
    }

    let showInfoWnd = !g_infoWindow.isShown() && !this.#activeTrack;

    if (this.#activeTrack) {
      this.#activeTrack.active = false;
    }
    g_infoWindow.close();

    if (ev.google_id) {
      assert(
        !g_map.locs.byGoogleId(ev.google_id),
        `[map] ${ev.google_id} already in cache`
      );
      this.showUpInfoWindow(
        g_map.locs.from({
          google_id: ev.google_id,
          provider: 'googlemaps',
        })
      );
      ev.stop();
    } else if (g_locInfoPanel.isShown()) {
      g_locInfoPanel.close();
    } else if (showInfoWnd) {
      g_infoWindow.show(
        g_map.locs.from({ lat: ev.lat, lng: ev.lng }),
        'map-interaction'
      );
    }
  }

  onmap_mousedown() {
    // If any popup is closed, then ignove click event.
    g_ignoreClickOnMap =
      g_autocomplete.closeIfOpen() ||
      g_controlsBar.closePopupIfOpen() ||
      (this.picPopup && this.picPopup.closeIfOpen());
  }

  onmap_dblclick() {
    g_infoWindow.cancelShow();
  }

  onmap_mousemove() {
    g_infoWindow.cancelShow('map-interaction');
  }

  onmap_trackactive(ev) {
    log(`[map:marker] '${ev.track.name}' track activated`);
    g_autocomplete.closeIfOpen();
    g_controlsBar.closePopupIfOpen();
    g_infoWindow.closeIfOpen();

    if (this.#activeTrack && ev.track != this.#activeTrack) {
      this.#activeTrack.active = false;
    }
    this.#activeTrack = ev.track;

    if (ev.track.loc) {
      g_locInfoPanel.show(ev.track.loc);
    } else if (ev.track.collection && g_map.isPickTrackControlActive()) {
      g_map.activePickControl.pickTrack(ev.track);
    } else {
      g_locInfoPanel.closeIfOpen();
    }

    this.trackEditToolbar.hidden = false;
  }

  onmap_trackclick(ev) {
    let collection = ev.track.collection;
    if (!collection) {
      return;
    }

    if (this.trackEditToolbar.isCutTrackOn()) {
      this.trackEditToolbar.cutTrackOff();
      collection.splitTrack(ev.track, {
        lat: ev.lat,
        lng: ev.lng,
      });
    }
  }

  onmap_trackinactive(ev) {
    log(`[map:marker] '${ev.track.name}' track deactivated`);
    this.#activeTrack = null;
    this.trackEditToolbar.hidden = true;
  }

  on_marker_click(loc) {
    log(`[map:marker] '${loc.name}' marker clicked`);
    if (
      g_autocomplete.closeIfOpen() ||
      g_controlsBar.closePopupIfOpen() ||
      g_infoWindow.closeIfOpen()
    ) {
      return;
    }

    if (this.#activeTrack) {
      this.#activeTrack.active = false;
    }

    if (
      g_map.activePickControl &&
      g_map.activePickControl.localName == 'pick-loc-control'
    ) {
      g_map.activePickControl.pickLocation(loc);
      return;
    }

    g_locInfoPanel.show(loc);
  }

  on_marker_dblclick(loc) {
    log(`[map:marker] marker '${loc.name}' accepted`);
    g_locInfoPanel.cancelShow();
    window.map_locationAccepted(loc);
  }

  #locDeleted(loc) {
    g_map.locs.removeLoc(loc);

    // Pop out a removed location from history.
    this.params.locId = undefined;
    navigation.replaceHistoryTo(this.page);
  }

  #locReported({ data, message }) {
    return app.contactUs({
      subject: `Request to delete '${data.name}' location`,
      message,
    });
  }

  #locSidePanelClosed() {
    g_topLeftContainer.removeAttribute('panelshown');

    this.params.locId = undefined;
    this.params.page = '';
    this.params.pageTitle = '';
    window.document.title = this.pageTitle;
    navigation.setHistoryTo(this.page);
  }

  // Updates the existing location information in DB or creates a new location.
  async #locChanged({ locData, track }) {
    let prevLoc = g_locInfoPanel.locInfo;
    let isNewLoc = !locData.id;

    let loc = await (isNewLoc
      ? g_map.locs.addLoc(locData)
      : g_map.locs.changeLoc(locData));
    if (!loc) {
      error(`[map] failed to ${isNewLoc ? 'add' : 'change'} location`, locData);
      return;
    }

    // Delete track from collection is the track was associated with the location.
    if (track && track.collection) {
      track.collection.deleteTrack(track);
    }

    // Reopen sidepanel to show a newly added location if the panel is still
    // shown for the same location.
    if (
      isNewLoc &&
      g_locInfoPanel.isShown() &&
      prevLoc == g_locInfoPanel.locInfo
    ) {
      g_locInfoPanel.showSync(loc);
    }

    // Zoom the map to ensure the marker is visible.
    if (g_map.zoom <= loc.typeInfo.zoom) {
      g_map.zoom = loc.typeInfo.zoom; // eslint-disable-line require-atomic-updates
    }
  }

  #locSidePanelShown(loc) {
    this.params.locId = loc.id;
    this.params.page = '';

    this.params.pageTitle = loc.id
      ? loc.title
      : 'Create a new location | CamperAid';

    window.document.title = this.pageTitle;
    if (loc.id && this.skipHistoryLocId != loc.id) {
      let page = this.getLocPage(loc);
      this.params.page = page;
      navigation.setHistoryTo(page);
    }

    g_topLeftContainer.setAttribute('panelshown', 'true');
  }

  #deleteTrack() {
    if (this.#activeTrack && this.#activeTrack.collection) {
      this.#activeTrack.collection.deleteTrack(this.#activeTrack);
    }
  }

  #activeTrack;
  #mapMediaLayer;
  #mapPromise;
  #keyUpHandler;
}

/**
 * Returns position of currently selected marker if any relative map's center.
 */
window.getPickedLocClientPos = async () => {
  let rect = document.getElementById('map').getBoundingClientRect();
  let info = g_locInfoPanel.locInfo || g_infoWindow.locInfo;
  let [x, y] = await Coords.toClient(await getMap(), info.lat, info.lng);
  log(`[map] getPickedLocClientPos: (${x}, ${y})`);
  return [
    x - Math.round((rect.right - rect.left) / 2),
    y - Math.round((rect.bottom - rect.top) / 2),
  ];
};

/**
 * Returns a list of all visible location names.
 */
window.getVisibleLocations = () =>
  getMap().then(map => map.locs.namesOfVisible());

/**
 * Returns a list of markers shown on screen.
 */
window.getOnScreenMarkers = () =>
  getMap().then(async map => {
    let markers = [];

    // Location markers.
    for (let l of map.locs.asList()) {
      if (l.marker) {
        markers.push(
          l.marker.getInViewPos(l.name).then(
            pos =>
              (pos && {
                name: l.name,
                lat: pos.lat,
                lng: pos.lng,
                x: pos.x,
                y: pos.y,
                type: 'standard',
              }) ||
              null
          )
        );
      }
    }

    // Info window temporary marker.
    if (g_infoWindow) {
      markers.push(
        g_infoWindow.tmpMarker.getInViewPos().then(
          pos =>
            (pos && {
              lat: pos.lat,
              lng: pos.lng,
              x: pos.x,
              y: pos.y,
              type: 'infoWindow_tmp',
            }) ||
            null
        )
      );
    }

    // MapLocInfo temporary marker.
    if (g_locInfoPanel) {
      markers.push(
        g_locInfoPanel.swapMarker.tmpMarker.getInViewPos().then(
          pos =>
            (pos && {
              lat: pos.lat,
              lng: pos.lng,
              x: pos.x,
              y: pos.y,
              type: 'locInfo_tmp',
            }) ||
            null
        )
      );
    }

    // Picture markers.
    for (let m of navigation.map.picMarkers.values()) {
      markers.push(
        m.getInViewPos().then(
          pos =>
            (pos && {
              name: m.title,
              lat: pos.lat,
              lng: pos.lng,
              x: pos.x,
              y: pos.y,
              type: 'pic',
            }) ||
            null
        )
      );
    }

    return (await Promise.all(markers)).filter(m => m);
  });

/**
 * Returns map center.
 */
window.getMapCenter = () => getMap().then(map => map.center);

window.zoomIn = () =>
  getMap().then(
    map =>
      new Promise(resolve => {
        map.once('zoom_changed', resolve);
        map.zoom += 1;
      })
  );
window.zoomOut = () =>
  getMap().then(
    map =>
      new Promise(resolve => {
        map.once('zoom_changed', resolve);
        map.zoom -= 1;
      })
  );
window.panTo = ({ lat, lng, zoom }) =>
  getMap().then(
    map =>
      new Promise(resolve => {
        map.once('bounds_changed', resolve);
        map.panTo({ lat, lng, zoom });
      })
  );

/**
 * Toggle the maps fullscreen mode.
 */
window.toggleFullscreen = function (ev) {
  if (ev.target.hasAttribute('fullscreen-mode')) {
    ev.target.removeAttribute('fullscreen-mode');
    ev.target.src = '/images/map-button-fullscreen-in.png';

    exitFullscreen();
  } else {
    ev.target.setAttribute('fullscreen-mode', 'true');
    ev.target.src = '/images/map-button-fullscreen-out.png';

    if ('requestFullscreen' in navigation.map) {
      navigation.map.requestFullscreen();
    } else if ('mozRequestFullScreen' in navigation.map) {
      navigation.map.mozRequestFullScreen();
    } else if ('webkitRequestFullScreen' in navigation.map) {
      navigation.map.webkitRequestFullScreen();
    }
  }
};

/**
 * Exits fullscreen mode.
 */
function exitFullscreen() {
  if (
    'fullscreenElement' in document && // Firefox
    !document.fullscreenElement
  ) {
    return null;
  }
  if (
    'webkitFullscreenElement' in document && // Chrome
    !document.webkitFullscreenElement
  ) {
    return null;
  }

  if ('exitFullscreen' in document) {
    // Firefox and Chrome
    return document.exitFullscreen();
  }
  if ('webkitCancelFullScreen' in document) {
    // Safari
    return document.webkitCancelFullScreen();
  }
  return null;
}

/**
 * Shows a info panel in 'add a new location' mode.
 */
window.map_addLocation = async loc => {
  g_infoWindow.closeIfOpen();

  let authenticated = await app.authenticate();
  if (!authenticated) {
    return;
  }

  let name = loc && loc.name;
  let google_id = loc && loc.google_id;
  let lat = (loc && loc.lat) || g_map.center.lat;
  let lng = (loc && loc.lng) || g_map.center.lng;

  g_locInfoPanel.show(
    new Location({
      name,
      type: 'toponym',
      google_id,
      lat,
      lng,
    })
  );
};

window.map_locationAccepted = loc =>
  (g_onacceptedCallback && g_onacceptedCallback(loc)) || null;

window.map_bounds = function () {
  let mapRect = document.getElementById('map').getBoundingClientRect();
  let autocompleteRect = g_autocomplete.getBoundingClientRect();
  let controlsBarRect = g_controlsBar.getBoundingClientRect();
  return {
    top: autocompleteRect.bottom,
    left: mapRect.left,
    right: controlsBarRect.left,
    bottom: mapRect.bottom,
  };
};

window.map_setLocationIfNeeded = data => data.id || g_map.locs.addLoc(data);
window.map_filterLocations = filter => g_map.locs.filter(filter);
window.map_setType = type => g_map.switchMap(type);

window.getAllLocations = () => getMap().then(map => map.locs);

window.getAllMediaTracks = () =>
  navigation.map.gpxCollections.flatMap(collection => collection.tracks);

navigation.registerPanel(MapPanel);

export { MapPanel };
